import React from 'react';
import { 
  Cpu, Speaker, Gamepad2, Smartphone, Watch, 
  Cable, Wrench, Shield, Percent, MessageCircle 
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { MembershipPlan } from './types';

interface DiscountRemindersCardProps {
  plan: MembershipPlan;
}

const discountsByPlan = {
  bronce: {
    'Tecnología': 5,
    'Audio': 5,
    'Gaming': 5,
    'Estuches': 5,
    'Relojes': 5,
    'Cargadores': 5,
    'Reparaciones': 10,
    'Vidrios': 5,
  },
  plata: {
    'Tecnología': 10,
    'Audio': 10,
    'Gaming': 10,
    'Estuches': 10,
    'Relojes': 10,
    'Cargadores': 10,
    'Reparaciones': 20,
    'Vidrios': 10,
  },
  oro: {
    'Tecnología': 15,
    'Audio': 20,
    'Gaming': 20,
    'Estuches': 20,
    'Relojes': 10,
    'Cargadores': 20,
    'Reparaciones': 30,
    'Vidrios': 15,
  },
};

const categoryIcons: Record<string, React.ElementType> = {
  'Tecnología': Cpu,
  'Audio': Speaker,
  'Gaming': Gamepad2,
  'Estuches': Smartphone,
  'Relojes': Watch,
  'Cargadores': Cable,
  'Reparaciones': Wrench,
  'Vidrios': Shield,
};

const planStyles = {
  bronce: {
    gradient: 'from-amber-900/30 to-amber-800/20',
    border: 'border-amber-700/30',
    badge: 'bg-amber-600/20 text-amber-400 border-amber-500/30',
    icon: 'text-amber-500',
    highlight: 'bg-amber-500/20 border-amber-500/40',
  },
  plata: {
    gradient: 'from-slate-700/30 to-slate-600/20',
    border: 'border-slate-500/30',
    badge: 'bg-slate-500/20 text-slate-300 border-slate-400/30',
    icon: 'text-slate-400',
    highlight: 'bg-slate-400/20 border-slate-400/40',
  },
  oro: {
    gradient: 'from-yellow-900/30 to-amber-800/20',
    border: 'border-yellow-600/30',
    badge: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    icon: 'text-yellow-500',
    highlight: 'bg-yellow-500/20 border-yellow-500/40',
  },
};

const DiscountRemindersCard: React.FC<DiscountRemindersCardProps> = ({ plan }) => {
  const discounts = discountsByPlan[plan];
  
  // Find the max discount for highlighting
  const maxDiscount = Math.max(...Object.values(discounts));

  return (
    <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-5 border border-white/10">
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <Percent className="w-5 h-5 text-blue-400" />
        <span className="text-white font-medium">Tus Descuentos Exclusivos</span>
      </div>
      
      {/* Discount Grid */}
      <div className="grid grid-cols-4 gap-2">
        {Object.entries(discounts).map(([category, discount]) => {
          const Icon = categoryIcons[category];
          const isMax = discount === maxDiscount;
          
          return (
            <div 
              key={category}
              className={`relative flex flex-col items-center p-2 rounded-xl border transition-all ${
                isMax 
                  ? 'bg-blue-500/20 border-blue-500/40' 
                  : 'bg-white/5 border-white/10 hover:bg-white/10'
              }`}
            >
              {isMax && (
                <span className="absolute -top-2 left-1/2 -translate-x-1/2 text-[9px] px-1.5 py-0.5 rounded-full bg-blue-500/20 text-blue-400 border border-blue-500/30 whitespace-nowrap">
                  MEJOR
                </span>
              )}
              <Icon className={`w-5 h-5 ${isMax ? 'text-blue-400' : 'text-white/60'} mb-1`} />
              <span className={`text-[10px] text-center leading-tight ${isMax ? 'text-white' : 'text-white/60'}`}>
                {category}
              </span>
              <span className={`text-sm font-bold ${isMax ? 'text-blue-400' : 'text-white'}`}>
                -{discount}%
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DiscountRemindersCard;
